import type { MoneyMinor } from "./model";

const POWERS_OF_TEN = [1, 10, 100, 1_000] as const;

export function isSafeMoneyMinor(value: unknown): value is MoneyMinor {
  return typeof value === "number" && Number.isSafeInteger(value);
}

export function isValidMinorUnitExponent(value: unknown): value is number {
  return (
    typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 3
  );
}

export function multiplyMoneyByQuantity(
  unitPriceMinor: MoneyMinor,
  quantityMilli: number,
): MoneyMinor {
  if (!isSafeMoneyMinor(unitPriceMinor) || unitPriceMinor < 0) {
    throw new RangeError("Unit price must be a non-negative safe integer.");
  }
  if (!Number.isSafeInteger(quantityMilli) || quantityMilli <= 0) {
    throw new RangeError("Quantity must be a positive safe integer.");
  }

  const product = BigInt(unitPriceMinor) * BigInt(quantityMilli);
  const rounded = (product + 500n) / 1_000n;
  const amount = Number(rounded);

  if (!Number.isSafeInteger(amount)) {
    throw new RangeError("Calculated amount is outside the safe integer range.");
  }

  return amount;
}

export function addMoney(values: readonly MoneyMinor[]): MoneyMinor {
  const total = values.reduce<bigint>((sum, value) => {
    if (!isSafeMoneyMinor(value)) {
      throw new RangeError("Money values must be safe integers.");
    }
    return sum + BigInt(value);
  }, 0n);
  const result = Number(total);

  if (!Number.isSafeInteger(result)) {
    throw new RangeError("Calculated total is outside the safe integer range.");
  }

  return result;
}

export function parseMoneyInput(
  rawValue: string,
  minorUnits: number,
): MoneyMinor | null {
  if (!isValidMinorUnitExponent(minorUnits)) {
    return null;
  }

  const normalized = rawValue.trim().replaceAll(",", "");
  const match = /^(\d+)(?:\.(\d*))?$/.exec(normalized);
  if (match === null) {
    return null;
  }

  const whole = match[1];
  if (whole === undefined) {
    return null;
  }

  const fraction = match[2] ?? "";
  if (fraction.length > minorUnits) {
    return null;
  }

  const factor = POWERS_OF_TEN[minorUnits];
  if (factor === undefined) {
    return null;
  }

  const paddedFraction = fraction.padEnd(minorUnits, "0");
  const minorValue = BigInt(whole) * BigInt(factor) + BigInt(paddedFraction || "0");
  const result = Number(minorValue);

  return Number.isSafeInteger(result) ? result : null;
}

export function parseQuantityInput(rawValue: string): number | null {
  const normalized = rawValue.trim();
  const match = /^(\d+)(?:\.(\d{0,3}))?$/.exec(normalized);
  if (match === null) {
    return null;
  }

  const whole = match[1];
  if (whole === undefined) {
    return null;
  }

  const fraction = (match[2] ?? "").padEnd(3, "0");
  const quantityMilli = BigInt(whole) * 1_000n + BigInt(fraction || "0");
  const result = Number(quantityMilli);

  return Number.isSafeInteger(result) && result > 0 ? result : null;
}

export function formatQuantity(quantityMilli: number): string {
  if (!Number.isSafeInteger(quantityMilli)) {
    return "";
  }

  const whole = Math.trunc(quantityMilli / 1_000);
  const fraction = Math.abs(quantityMilli % 1_000)
    .toString()
    .padStart(3, "0")
    .replace(/0+$/, "");

  return fraction.length > 0 ? `${whole}.${fraction}` : String(whole);
}

export function formatMoneyInput(
  valueMinor: MoneyMinor | null,
  minorUnits: number,
): string {
  if (valueMinor === null || !isValidMinorUnitExponent(minorUnits)) {
    return "";
  }

  const factor = POWERS_OF_TEN[minorUnits];
  if (factor === undefined) {
    return "";
  }

  const whole = Math.trunc(valueMinor / factor);
  const fraction = Math.abs(valueMinor % factor)
    .toString()
    .padStart(minorUnits, "0");

  return minorUnits === 0 ? String(whole) : `${whole}.${fraction}`;
}

export function formatMoney(
  valueMinor: MoneyMinor,
  currency: string,
  minorUnits: number,
): string {
  if (!isSafeMoneyMinor(valueMinor) || !isValidMinorUnitExponent(minorUnits)) {
    return "Invalid amount";
  }

  const factor = POWERS_OF_TEN[minorUnits];
  if (factor === undefined) {
    return "Invalid amount";
  }

  try {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency,
      minimumFractionDigits: minorUnits,
      maximumFractionDigits: minorUnits,
    }).format(valueMinor / factor);
  } catch {
    return `${currency} ${(valueMinor / factor).toFixed(minorUnits)}`;
  }
}
